import { eqDeepStrict } from '@ipfabric/utils/fp/comparison';
import { keepIdentityIfEqual } from '@ipfabric/utils/fp/keepIdentityIfEqual';
import { plainSet } from '@ipfabric/utils/fp/plainSet';
import {
  type GraphMetadata,
  type GraphState,
  type TopologyGraphMetadata,
  emptyAvailableEntities,
  initialTechnologies,
  isGroupSettings,
  isTopologyGraphMetadata,
} from 'components/Graph/GraphState';

import {
  AbsurdIpfError,
  IpfError,
} from '@ipfabric/frontend-shared-assets/utils/IpfError';
import {
  type CollapsedNodeGroup,
  type DeviceGroupAttribute,
  type GraphLayoutRequest,
  type GraphMode,
  type GraphOverlayRequest,
  type GraphParameters,
  type GraphPath,
  type GraphPathInfo,
  type GraphPathLookupParameters,
  type GraphRequestBody,
  type GraphResponseBody,
  type GraphTopologyParameters,
  type NodePositions,
  type NodeSn,
  type PathLookupGraphPreset,
  type TopologyGraphPreset,
  assertType,
  getRecordEntries,
  getRecordKeys,
  graphOverlayToRequest,
  isCompareGraphOverlayRequest,
  isPathLookupRequestParameters,
  isTopologyGraphPreset,
  isTopologyRequestParameters,
  safeAbsurd,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import {
  savePathLookupAdvancedSettingsPresetIdToLocalStorage,
  savePathLookupParametersToLocalStorage,
} from 'components/Graph/GraphSidePanel/GraphPathlookup/PathLookupLocalStorage';
import { hydrateGraph } from 'components/Graph/helpers/hydrateGraph';
import { emptyCallbackHistory } from 'utils/history/CallbackHistory';
import { omit } from 'utils/object/omit';

import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { fitGraphToView } from 'components/Graph/commands/fitGraphToView';
import { getPresetFunctionByMode } from 'components/Graph/commands/helpers/getPresetFunctionByMode';
import { withGraphsLoadingIndicator } from 'components/Graph/commands/helpers/withGraphsLoadingIndicator';
import { hideGraphLayoutDialog } from 'components/Graph/commands/hideGraphLayoutDialog';
import { loadEmptyGraph } from 'components/Graph/commands/loadEmptyGraph';
import { closeNodeContextMenu } from 'components/Graph/commands/nodes/closeNodeContextMenu';
import { setPathLayouts } from 'components/Graph/commands/setPathLayouts';
import { showSidePanel } from 'components/Graph/commands/showSidePanel';
import { updateLoadingVersion } from 'components/Graph/commands/updateLoadingVersion';
import { createGraphApiRequest } from 'components/Graph/helpers/createGraphApiRequest';
import { getNodePositionsFromState } from 'components/Graph/helpers/getNodePositions';
import { array, set } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { produce } from 'immer';
import { curryDispatch } from 'utils/react/useCommandDispatcher';
import { clearSharedView } from '../helpers/clearSharedView';
import type { GraphCommandFactory } from '../model/GraphCommandContext';
import { ensureNodesInView } from './ensureNodesInView';
import { refreshPathInspectorPacketsDetail } from './pathLookup/helpers/refreshPathInspectorPacketsDetail';
import { refreshDeviceExplorerEntities } from './refreshDeviceExplorerEntities';
import { updateStpInstanceIsolate } from './updateStpInstanceIsolate';

export interface LoadGraphCommand {
  collapsedNodeGroups?: CollapsedNodeGroup[];
  hiddenNodeSns?: NodeSn[];
  /**
   * Explicitly set node positions; if not set, positions are either reset from server or used
   * from last loaded graph - see `shouldTransferGraphState` function for decision logic on this.
   * Hint: send empty object here to force reset node positions.
   */
  nodePositions?: NodePositions;
  /**
   * keep - keep currently used overlay (not applicable when pathlookup/topology graph mode changes,
   *        in that case 'keep' is treated as 'none')
   * none - reset to no overlay
   */
  overlay: GraphOverlayRequest | 'keep' | 'none';
  parameters?: GraphParameters;
  /**
   * By default, graph load clears undo/redo history because we could barely control
   * what led to the reload and how to properly undo it. So, undo/redo should be handled
   * by particular undoable commands, and these commands should call loadGraph with this
   * flag to prevent clearing history.
   */
  preserveHistory?: boolean;
  /**
   * Override autodetection whether to transfer previous state
   *
   * @see shouldTransferGraphState
   */
  transferPreviousState?: boolean;
}

const shouldTransferPathLookupState = (
  parameters: GraphPathLookupParameters,
  prevParameters: GraphPathLookupParameters,
): boolean => {
  switch (parameters.pathLookupType) {
    case 'hostToDefaultGW':
      return (
        prevParameters.pathLookupType === 'hostToDefaultGW' &&
        parameters.startingPoint === prevParameters.startingPoint
      );
    case 'multicast':
      return (
        prevParameters.pathLookupType === 'multicast' &&
        parameters.source === prevParameters.source &&
        parameters.group === prevParameters.group
      );
    case 'unicast':
      return (
        prevParameters.pathLookupType === 'unicast' &&
        parameters.startingPoint === prevParameters.startingPoint &&
        parameters.destinationPoint === prevParameters.destinationPoint
      );
    default:
      safeAbsurd(parameters, 'unknown parameters type');
      return false;
  }
};

const shouldTransferTopologyState = (
  parameters: GraphTopologyParameters,
  prevParameters: GraphTopologyParameters,
): boolean => parameters.groupBy === prevParameters.groupBy;

/**
 * Determine whether newly loaded graph is considered to be an update of current graph
 * to transfer some things from previous state:
 *  - node positions
 *  - hidden nodes
 *  - selected nodes
 *  - collapsed nodes
 *  - viewport position (see maybeFitToViewport)
 */
const shouldTransferGraphState = (
  parameters: GraphParameters,
  prevGraphMetadata: GraphMetadata | null,
): boolean => {
  if (parameters.type !== prevGraphMetadata?.parameters.type) {
    return false;
  }
  if (isTopologyRequestParameters(parameters)) {
    assertType(isTopologyRequestParameters, prevGraphMetadata?.parameters);
    return shouldTransferTopologyState(
      parameters,
      prevGraphMetadata.parameters,
    );
  }
  if (isPathLookupRequestParameters(parameters)) {
    assertType(isPathLookupRequestParameters, prevGraphMetadata?.parameters);
    return shouldTransferPathLookupState(
      parameters,
      prevGraphMetadata.parameters,
    );
  }
  safeAbsurd(parameters, 'unknown parameters type');
  return false;
};

/**
 * This function decides which paths should have the "nodes repositioned" flag
 * after topology graph load.
 */
const preserveRepositionedNodes = (
  prevMetadata: GraphMetadata | null,
  parameters: GraphTopologyParameters,
  foundPaths: GraphPath[],
): Set<GraphPath> => {
  const layoutingRequests = pipe(
    parameters.layouts ?? [],
    array.partition((layout) => layout.nodeSns === undefined),
  );
  const fullyRelayoutedPaths = layoutingRequests.right.map(prop('path'));
  const partiallyRelayoutedPaths = new Set(
    layoutingRequests.left
      // drop layout requests where empty node array was sent since they're effectively no-op
      .filter((layout) => layout.nodeSns!.length > 0)
      .map(prop('path')),
  );

  const isStillRepositionedPath = (key: GraphPath) =>
    // only paths that were retrieved with last graph response may keep being repositioned
    foundPaths.includes(key) &&
    // paths which were requested to recalculate layout for whole path aren't repositioned anymore
    !fullyRelayoutedPaths.includes(key);

  return pipe(
    isTopologyGraphMetadata(prevMetadata)
      ? prevMetadata.nodesRepositioned
      : new Set<GraphPath>(),
    set.filter(isStillRepositionedPath),
    plainSet.union(partiallyRelayoutedPaths),
  );
};

const addPinnedLayouts = (
  parameters: GraphTopologyParameters,
  prevMetadata: TopologyGraphMetadata,
): GraphTopologyParameters =>
  produce(parameters, (draft) => {
    // pin paths with repositioned nodes that don't have another layouting request in current parameters
    const pinPositionLayoutRequests = pipe(
      prevMetadata.nodesRepositioned,
      plainSet.toArray,
      array.filter((p) =>
        (parameters.layouts ?? array.empty).every(({ path }) => p !== path),
      ),
      array.map((path): GraphLayoutRequest => ({ path, layout: 'pinned' })),
    );
    if (pinPositionLayoutRequests.length === 0) {
      return;
    }
    draft.layouts = draft.layouts || [];
    draft.layouts.push(...pinPositionLayoutRequests);
  });

const getParametersForRequest =
  (positions: NodePositions | undefined, state: GraphState) =>
  (parameters: GraphParameters) =>
    positions &&
    isTopologyRequestParameters(parameters) &&
    isTopologyGraphMetadata(state.graphMetadata) &&
    !parameters.recalculateLayout
      ? addPinnedLayouts(parameters, state.graphMetadata)
      : parameters;

const processStpInstances =
  (preset: TopologyGraphPreset | PathLookupGraphPreset) =>
  (parameters: GraphParameters) => {
    if (
      isTopologyRequestParameters(parameters) &&
      isTopologyGraphPreset(preset)
    ) {
      const groups = preset.edges.filter(isGroupSettings);
      const stpGroup = groups.find(({ children }) =>
        children.some(({ name }) => name === 'stp'),
      );

      const sendStpInstances =
        stpGroup === undefined ||
        stpGroup?.visible ||
        parameters.technologies?.stpInstances?.isolate;

      return sendStpInstances
        ? parameters
        : {
            ...parameters,
            technologies: {
              ...parameters.technologies,
              stpInstances: undefined,
            },
          };
    }
    return parameters;
  };

export const loadGraph: GraphCommandFactory<LoadGraphCommand> = (
  cmdContext,
  dispatch,
) => {
  const getOverlayToApply = (
    overlay: LoadGraphCommand['overlay'],
    parametersType: GraphMode,
  ): GraphOverlayRequest | undefined => {
    switch (overlay) {
      case 'keep': {
        return parametersType ===
          cmdContext.getState().graphMetadata?.parameters.type
          ? graphOverlayToRequest(cmdContext.getState().overlay)
          : undefined;
      }
      case 'none': {
        return undefined;
      }
      default: {
        return overlay;
      }
    }
  };

  const setComparingSameSnapshot = () => {
    cmdContext.updateState((draft) => {
      draft.overlay = {
        type: 'compare',
        snapshotToCompare: cmdContext.getState().snapshotId,
      };
      draft.loading = 'LOADED';
    });
  };

  const isComparingSameSnapshot = (overlay: GraphOverlayRequest | undefined) =>
    overlay &&
    isCompareGraphOverlayRequest(overlay) &&
    cmdContext.getState().snapshotId === overlay.snapshotToCompare;

  const getParametersToApply = (
    command: LoadGraphCommand,
  ): GraphParameters | null =>
    command.parameters ??
    cmdContext.getState().graphMetadata?.parameters ??
    null;

  const updateTopologyLayoutsFromResponse = (
    foundPaths: Record<GraphPath, GraphPathInfo>,
    groupBy: DeviceGroupAttribute,
  ) => {
    pipe(
      foundPaths,
      getRecordEntries,
      array.map(([path, { defaultLayout }]) => ({
        path,
        layout: defaultLayout,
      })),
      (options) => ({
        groupBy,
        options,
      }),
      curryDispatch(dispatch)(setPathLayouts),
    );
  };

  /**
   * Always fit new content to viewport, fit on updates only when needed
   */
  const maybeFitToViewport = (graphStateTransfer: boolean) => {
    // delay event to next tic to wait for the result to be rendered and graph bounds updated
    setTimeout(() => {
      if (graphStateTransfer) {
        dispatch(ensureNodesInView, { strict: false });
      } else {
        dispatch(fitGraphToView);
      }
    }, 0);
  };

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
  return async (command) => {
    // cleanup things that'll probably become irrelevant after graph load
    dispatch(closeNodeContextMenu);
    dispatch(hideGraphLayoutDialog);

    const parameters = getParametersToApply(command);

    if (parameters === null) {
      stumble('Trying to reload graph when none is loaded');
      return;
    }

    const overlay = getOverlayToApply(command.overlay, parameters.type);
    if (isComparingSameSnapshot(overlay)) {
      setComparingSameSnapshot();
      return;
    }

    // handle all graph mode specific stuff before sending request here
    if (isPathLookupRequestParameters(parameters)) {
      savePathLookupParametersToLocalStorage(parameters);
      if (
        parameters.pathLookupType === 'unicast' ||
        parameters.pathLookupType === 'multicast'
      ) {
        savePathLookupAdvancedSettingsPresetIdToLocalStorage(
          cmdContext.getState().pathlookup.selectedAdvancedOptions.id,
        );
      }
    } else if (isTopologyRequestParameters(parameters)) {
      if (parameters.paths.length === 0) {
        dispatch(loadEmptyGraph);
        return;
      }
      // Ensure ungrouping logic is handled in case of initialization form external parameters
      if (parameters.technologies?.stpInstances?.isolate) {
        dispatch(updateStpInstanceIsolate, {
          isolate: parameters.technologies.stpInstances.isolate,
        });
      }
    } else {
      throw new AbsurdIpfError(parameters, 'unknown parameters type');
    }

    const graphMode = parameters.type;
    const state = cmdContext.getState();
    const preset = getPresetFunctionByMode[graphMode](state);
    const graphStateTransfer =
      command.transferPreviousState ??
      shouldTransferGraphState(parameters, state.graphMetadata);
    const positions =
      command.nodePositions ??
      (graphStateTransfer ? getNodePositionsFromState(state) : undefined);

    dispatch(updateLoadingVersion);
    const { loadingVersion } = cmdContext.getState();
    const graphResponse = await withGraphsLoadingIndicator(cmdContext)(
      async (terminate) => {
        const result = await cmdContext.api.abortableRequest<
          GraphResponseBody,
          GraphRequestBody
        >({
          ...createGraphApiRequest({
            overlay,
            parameters: pipe(
              parameters,
              getParametersForRequest(positions, state),
              processStpInstances(preset),
            ),
            positions,
            settings: preset,
            snapshotId: state.snapshotId,
            attributeFilters: state.attributeFilters,
          }),
          abortSignal: cmdContext.singletonControllers.nextSignal('loadGraph'),
        });
        if (
          loadingVersion !== cmdContext.getState().loadingVersion ||
          result.aborted
        ) {
          terminate();
          return null;
        }

        return result.data;
      },
    );

    if (graphResponse === null) {
      // aborted because another request was fired meanwhile
      return;
    }

    cmdContext.updateState((draft) => {
      draft.firstLoading = false;
      draft.graph = hydrateGraph(
        graphResponse.graphResult,
        graphStateTransfer ? draft.graph : undefined,
        command.collapsedNodeGroups,
        command.hiddenNodeSns,
      );
      if (!command.preserveHistory) {
        draft.history = emptyCallbackHistory;
      }
      if (Object.keys(draft.graph.nodes).length > 0) {
        draft.longRendering = true;
      }
      draft.overlay = graphResponse.overlay ?? null;
      draft.emptyGraphReason = graphResponse.emptyGraphReason ?? null;

      clearSharedView(draft);
    });

    // handle all graph mode specific state update here
    if (isTopologyRequestParameters(parameters)) {
      if (!graphResponse.topology && !graphResponse.emptyGraphReason) {
        stumble('Expected API to return topology info for topology request');
      }
      const foundPathInfos = graphResponse.topology?.paths ?? {};
      const foundPaths = getRecordKeys(foundPathInfos);
      cmdContext.updateState((draft) => {
        draft.graphMetadata = {
          allPathsSelected: graphResponse.topology?.allPathsSelected ?? false,
          availableEntities:
            graphResponse.topology?.availableEntities ?? emptyAvailableEntities,
          nodesRepositioned: parameters.recalculateLayout
            ? // reset repositioned nodes when site was recalculated
              new Set()
            : preserveRepositionedNodes(
                state.graphMetadata,
                parameters,
                foundPaths,
              ),
          parameters: keepIdentityIfEqual<GraphTopologyParameters>(
            eqDeepStrict,
          )(
            // we fool Typescript here - undefined or path lookup parameters may come,
            // but they won't be equal to new value and never make it to result.
            draft.graphMetadata?.parameters as GraphTopologyParameters,
          )(
            // customized layouts should not be remembered - they just serve to recalculate layout
            // for given paths, but in ongoing scenarios we should respect actual node positions again.
            omit(['layouts', 'recalculateLayout'], parameters),
          ),
          paths: foundPathInfos,
          snapshotId: state.snapshotId,
        };
        draft.technologies.selectedTechnologies =
          parameters.technologies ?? initialTechnologies;
        draft.sidePanel.pathLookupTabId = 'dataInput';
        draft.sidePanel.pathLookupDataInputTabId = 'unicast';
      });
      updateTopologyLayoutsFromResponse(foundPathInfos, parameters.groupBy);
      if (state.sidePanel.open && state.sidePanel.contentId === 'pathlookup') {
        dispatch(showSidePanel, { networkViewerTabId: 'dataInput' });
      }
    } else if (isPathLookupRequestParameters(parameters)) {
      const pathLookupResponse = graphResponse.pathlookup;
      if (graphResponse.emptyGraphReason) {
        return;
      }
      if (!pathLookupResponse) {
        throw new IpfError(
          'INVALID_STATE',
          'Expected API to return pathlookup info for pathlookup request',
        );
      }
      cmdContext.updateState((draft) => {
        draft.graphMetadata = {
          decisions: pathLookupResponse.decisions,
          eventsSummary: pathLookupResponse.eventsSummary,
          initialExpectedPassingTraffic: pathLookupResponse.check.exists
            ? (pathLookupResponse.check.expectedPassingTraffic ?? null)
            : null,
          parameters,
          passingTraffic: pathLookupResponse.passingTraffic ?? null,
          snapshotId: state.snapshotId,
        };
        draft.sidePanel.networkViewerTabId = 'dataInput';
        // Clear network viewer data input form
        draft.networkViewer.groupBy = null;
      });
      assertType(isPathLookupRequestParameters, parameters);
      if (
        state.sidePanel.open &&
        ((state.sidePanel.contentId === 'pathlookup' &&
          state.sidePanel.pathLookupTabId === 'dataInput') ||
          state.sidePanel.contentId === 'networkViewer')
      ) {
        dispatch(showSidePanel, {
          pathLookupDataInputTabId: parameters.pathLookupType,
        });
      } else {
        // do not show side panel, just make sure corresponding data input tab is selected when user opens
        // data input
        cmdContext.updateState((draft) => {
          draft.sidePanel.pathLookupDataInputTabId = parameters.pathLookupType;
        });
      }
      cmdContext.updateState((draft) => {
        refreshPathInspectorPacketsDetail(
          draft.graph,
          draft.pathlookup.pathInspector.selectedPackets,
        );
      });
    } else {
      safeAbsurd(parameters, 'unknown parameters type');
    }

    dispatch(refreshDeviceExplorerEntities);

    maybeFitToViewport(graphStateTransfer);
  };
};
